import React, { useState, useRef } from 'react'
import { exportDashboardAsPDF } from '../../utils/pdfExport'
import './ExportButton.css'

interface ExportButtonProps {
    pages?: { url: string; title: string }[]
    title?: string
    className?: string
    dashboardType?: 'bcbs' | 'pmh' | 'tmb' | 'hdl'
}

const ExportButton: React.FC<ExportButtonProps> = ({ pages, title = "Dashboard", className = '', dashboardType }) => {
    const [loading, setLoading] = useState(false)
    const [progress, setProgress] = useState(0)
    const [exportFormat, setExportFormat] = useState<'pdf' | 'pptx'>('pdf')
    const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

    const getDashboardType = (): 'bcbs' | 'pmh' | 'tmb' | 'hdl' | null => {
        if (dashboardType) return dashboardType
        const lowerTitle = title.toLowerCase()
        if (lowerTitle.includes('pmh') || lowerTitle.includes('perinatal mental health')) return 'pmh'
        if (lowerTitle.includes('tmb') || lowerTitle.includes('team birth')) return 'tmb'
        if (lowerTitle.includes('bcbs') || lowerTitle.includes('designation')) return 'bcbs'
        if (lowerTitle.includes('hdl') || lowerTitle.includes('huddle')) return 'hdl'
        return null
    }

    const resetState = () => {
        if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current)
            progressIntervalRef.current = null
        }
        setLoading(false)
        setProgress(0)
    }

    const handleServerSideExport = async (dashboard: string, hospitalIds: string[], format: 'pdf' | 'pptx' = 'pdf') => {
        const isMultiSelectStr = sessionStorage.getItem('isMultiSelect')
        const hospitalNamesRaw = sessionStorage.getItem('hospitalNames')
        const isMultiSelect = isMultiSelectStr === 'true'
        const namesMap: Record<string, string> = hospitalNamesRaw ? JSON.parse(hospitalNamesRaw) : {}

        let hospitalName = null
        if (hospitalIds.length === 1) {
            hospitalName = namesMap[hospitalIds[0]] || null
        }

        const requestBody: Record<string, unknown> = {
            hospitalIds,
            isMultiSelect,
            hospitalName,
            hospitalNames: namesMap
        }

        if (dashboard === 'bcbs') {
            requestBody.timePeriods = {
                outcome: sessionStorage.getItem('outcome_measures_timePeriod') || 'monthly',
                ntsvRaceGroups: sessionStorage.getItem('outcome_ntsv_race_groups'),
                prem: sessionStorage.getItem('prem_completion_timePeriod') || 'monthly',
                premAgreement: {
                    timePeriod: sessionStorage.getItem('prem_agreement_timePeriod') || 'monthly',
                    question: sessionStorage.getItem('prem_agreement_question') || '',
                    raceGroups: sessionStorage.getItem('prem_agreement_race_groups')
                },
                premDisagreement: {
                    timePeriod: sessionStorage.getItem('prem_disagreement_timePeriod') || 'monthly',
                    question: sessionStorage.getItem('prem_disagreement_question') || '',
                    raceGroups: sessionStorage.getItem('prem_disagreement_race_groups')
                }
            }
        } else if (dashboard === 'tmb') {
            const tmbSliderRange = sessionStorage.getItem(`tmb_slider_${hospitalIds.join('_')}`)

            requestBody.pageSettings = {
                populationCharacteristics: {
                    sliderRange: tmbSliderRange
                },
                huddleParticipation: {
                    sliderRange: tmbSliderRange
                },
                boardUse: {
                    sliderRange: tmbSliderRange
                },
                boardUseComments: {
                    sliderRange: tmbSliderRange
                },
                generalPatientComments: {
                    sliderRange: tmbSliderRange
                },
                agreementSingle: {
                    sliderRange: tmbSliderRange,
                    question: sessionStorage.getItem('agreement_single_question'),
                    stratification: sessionStorage.getItem('agreement_single_stratification')
                },
                agreementAcross: {
                    sliderRange: tmbSliderRange,
                    questions: sessionStorage.getItem('agreement_across_questions'),
                    stratification: sessionStorage.getItem('agreement_across_stratification')
                },
                agreementEthnicity: {
                    sliderRange: tmbSliderRange,
                    question: sessionStorage.getItem('agreement_ethnicity_question'),
                    stratification: sessionStorage.getItem('agreement_ethnicity_stratification'),
                    raceGroups: sessionStorage.getItem('agreement_ethnicity_race_groups')
                },
                agreementInsurance: {
                    sliderRange: tmbSliderRange,
                    question: sessionStorage.getItem('agreement_insurance_question'),
                    stratification: sessionStorage.getItem('agreement_insurance_stratification'),
                    insuranceTypes: sessionStorage.getItem('agreement_insurance_types')
                },
                agreementEducation: {
                    sliderRange: tmbSliderRange,
                    question: sessionStorage.getItem('agreement_education_question'),
                    stratification: sessionStorage.getItem('agreement_education_stratification'),
                    educationTypes: sessionStorage.getItem('agreement_education_types')
                },
                agreementDelivery: {
                    sliderRange: tmbSliderRange,
                    question: sessionStorage.getItem('agreement_delivery_question'),
                    stratification: sessionStorage.getItem('agreement_delivery_stratification')
                },
                agreementComplications: {
                    sliderRange: tmbSliderRange,
                    question: sessionStorage.getItem('agreement_complications_question'),
                    stratification: sessionStorage.getItem('agreement_complications_stratification'),
                    complications: sessionStorage.getItem('agreement_complications_types')
                },
                disagreementSingle: {
                    sliderRange: tmbSliderRange,
                    question: sessionStorage.getItem('disagreement_single_question'),
                    stratification: sessionStorage.getItem('disagreement_single_stratification')
                },
                disagreementAcross: {
                    sliderRange: tmbSliderRange,
                    questions: sessionStorage.getItem('disagreement_across_questions'),
                    stratification: sessionStorage.getItem('disagreement_across_stratification')
                },
                patientSurveys: {
                    sliderRange: tmbSliderRange
                },
                outcomeMeasures: {
                    sliderRange: tmbSliderRange,
                    timePeriod: sessionStorage.getItem('tmb_outcome_measures_timePeriod') || 'monthly'
                }
            }
        } else if (dashboard === 'hdl') {
            const hdlSliderRange = sessionStorage.getItem(`hdl_slider_${hospitalIds.join('_')}`)
            requestBody.pageSettings = {
                sliderRange: hdlSliderRange
            }
        }

        setProgress(5)

        let simulatedProgress = 5
        progressIntervalRef.current = setInterval(() => {
            simulatedProgress += 3
            if (simulatedProgress < 85) {
                setProgress(simulatedProgress)
            }
        }, 500)

        const response = await fetch(`/api/${format}/${dashboard}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody)
        })

        if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current)
            progressIntervalRef.current = null
        }

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}))
            throw new Error(errorData.details || errorData.error || `${format.toUpperCase()} generation failed`)
        }

        setProgress(90)

        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${title.replace(/\s+/g, '_')}_Report_${new Date().toISOString().split('T')[0]}.${format}`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)

        setProgress(100)
    }

    const handleExport = async (e: React.MouseEvent) => {
        e.preventDefault()
        if (loading) return

        const savedIds = sessionStorage.getItem('selectedHospitalIds')
        const hospitalIds = savedIds ? JSON.parse(savedIds) : []

        if (hospitalIds.length === 0) {
            alert('Please select a hospital first')
            return
        }

        const dashboard = getDashboardType()
        const format: 'pdf' | 'pptx' = dashboard === 'tmb' ? 'pptx' : 'pdf'

        setLoading(true)
        setProgress(0)
        setExportFormat(format)

        try {
            if (dashboard === 'bcbs' || dashboard === 'tmb' || dashboard === 'hdl') {
                await handleServerSideExport(dashboard, hospitalIds, format)
            } else if (pages) {
                await exportDashboardAsPDF(pages, title, (prog) => {
                    setProgress(prog)
                })
            }

            setTimeout(() => resetState(), 800)
        } catch (error) {
            console.error(`${format.toUpperCase()} export error:`, error)
            alert(`Failed to export ${format.toUpperCase()}: ${error instanceof Error ? error.message : 'Unknown error'}`)
            resetState()
        }
    }

    return (
        <a
            href="#"
            className={`export-button ${loading ? 'loading' : ''} ${className}`}
            onClick={handleExport}
            title="Download Report"
        >
            <span className="label">
                {loading ? `Generating ${exportFormat.toUpperCase()}... ${progress}%` : 'Download'}
            </span>
            {loading && (
                <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                </div>
            )}
        </a>
    )
}

export default ExportButton
